/**
 * @desc The admin usage overview (H2-266): agent activity across the beta, by UTC day, read from
 * the usage ledger's event log (`agent_usage_events`).
 *
 * Every measure is counted from **recorded model calls**. A turn that never reached the model — a
 * quota refusal, a turn that failed before its first call, a call whose ledger append was lost —
 * leaves no row, so it is in none of these figures: `questions` in particular is "questions the
 * model answered at least one call for", an undercount of questions asked. Days are UTC calendar
 * days, the same grain the quota enforces in.
 *
 * A separate namespace from `AgentUsage`, which is the per-caller quota read side; this one is the
 * cross-user reporting surface and has nothing to do with allowances.
 */
export namespace UsageOverview {
  /** @desc Longest span one request may cover, in days (inclusive of both ends). */
  export const MAX_RANGE_DAYS = 400;

  /**
   * @desc Calendar months the ledger's events are retained for (`TTL ts + INTERVAL 13 MONTH` in
   * `internal/clickhouse/4_agent_usage.sql`). A range starting further back would read a log that
   * has already been partly expired, so it is refused rather than reported as low usage.
   */
  export const RETENTION_MONTHS = 13;

  /** @desc A UTC calendar day, `YYYY-MM-DD` — the shape every range bound and series key takes. */
  export const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

  /**
   * @desc The ledger's `org_id` for activity whose token carried no organisation claim. Shown as
   * its own visible bucket ({@link NO_ORGANISATION_NAME}), never dropped, and never counted as an
   * organisation in `activeOrgs`. Excluded together with the internal organisations when
   * `excludeInternal` is on: an org-less token is a staff, test or pre-onboarding account, not a
   * customer, which is what the toggle exists to isolate.
   */
  export const NO_ORGANISATION = '';
  export const NO_ORGANISATION_NAME = 'No organisation';

  /** @desc The five measures, over a day or over a whole range. */
  export type Measures = {
    /** @desc Weighted credits, as charged by the quota (not raw tokens). */
    credits: number;
    activeUsers: number;
    /** @desc Distinct organisations; the {@link NO_ORGANISATION} bucket is not one. */
    activeOrgs: number;
    activeConversations: number;
    /** @desc Distinct turns with at least one recorded model call. */
    questions: number;
  };

  /** @desc One UTC day of the series. Days without recorded activity are present, as zeros. */
  export type Day = Measures & { day: string };

  /** @desc One organisation's activity over the range; `orgId` may be {@link NO_ORGANISATION}. */
  export type OrganisationUsage = Omit<Measures, 'activeOrgs'> & { orgId: string };

  /** @desc What the store reads for a range: the series, its range-wide distincts, and per org. */
  export type Figures = {
    days: Day[];
    /**
     * @desc Distinct counts over the WHOLE range — a user active on five days counts once — so
     * this is not the sum of `days`. Only `credits` sums.
     */
    summary: Measures;
    /** @desc Every organisation with recorded activity in the range, most credits first. */
    byOrganisation: OrganisationUsage[];
  };

  /** @desc An organisation for the filter UI, with whether — and why — it counts as internal. */
  export type Organisation = {
    id: string;
    name: string;
    /** @desc `allowListed || flagged`: excluded when `excludeInternal` is on. */
    internal: boolean;
    /** @desc On the hard-coded staff allow-list (`Organisation.internalOrganisations`); fixed. */
    allowListed: boolean;
    /** @desc Flagged internal by an admin (`organisations.setInternal`); reversible. */
    flagged: boolean;
  };

  /** @desc Most organisations one request may filter on. */
  export const MAX_ORG_FILTER = 200;

  /**
   * @desc The overview's filters. They compose by intersection, the exclusion included: with
   * `excludeInternal` on, an internal organisation (or an internal user's activity) reads as zero
   * even when named in `orgIds`/`userId`, because the toggle promises those rows are absent from
   * every measure. A UI narrowing to an internal organisation should turn the toggle off.
   */
  export type Input = {
    /** @desc First UTC day, inclusive. */
    from: string;
    /** @desc Last UTC day, inclusive. A day after today is clamped to today. */
    to: string;
    /** @desc Only these organisations ({@link NO_ORGANISATION} allowed). Empty means no filter. */
    orgIds?: string[] | undefined;
    userId?: string | undefined;
    /** @desc Drop internal organisations and the no-organisation bucket. Defaults to `true`. */
    excludeInternal?: boolean | undefined;
  };

  export type Overview = Figures & {
    /** @desc The range actually read, inclusive, after clamping `to` to today. */
    from: string;
    to: string;
    excludeInternal: boolean;
    /** @desc The organisation ids the toggle excluded, {@link NO_ORGANISATION} included. */
    excludedOrgIds: string[];
    /** @desc `AGENT_USAGE_LEDGER_DISABLED` is on: recent days read as zero because nothing is recorded. */
    ledgerDisabled: boolean;
    /** @desc Every WorkOS organisation, by name, for the filter. */
    organisations: Organisation[];
  };
}
